import { useTranslation } from 'react-i18next';
import { getRouteApi } from '@tanstack/react-router';

import { useSubmission } from '@/api/generated';
import Centered from '@/components/blocks/layout/centered';
import PageLayout from '@/components/blocks/layout/page-layout';
import {
  NavBreadcrumb,
  NavBreadcrumbItem,
} from '@/components/blocks/shared/nav-breadcrumb';
import {
  isShelfCompleted,
  ShelveCard,
} from '@/components/blocks/shelves/shelve-card';
import { ShelvesSkeleton } from '@/components/blocks/shelves/shelves-skeleton';
import { CommonError } from '@/components/ui/common-error';

const route = getRouteApi('/app/shelves/$customerId/$submissionId');

const Shelves = () => {
  const { t } = useTranslation();
  const { customerId, submissionId } = route.useParams();
  const { data, isLoading, isError } = useSubmission(submissionId);

  const navigationCrumbs: Array<NavBreadcrumbItem> = [
    {
      text: t('customers.title'),
      path: '/app/shelves',
    },
    {
      text: `${customerId}`,
      path: '/app/shelves/$customerId',
      params: { customerId },
    },
    {
      text: t('shelves.title', { id: submissionId }),
      path: '/app/shelves/$customerId/$submissionId',
      params: { customerId, submissionId },
    },
  ];

  if (isLoading) {
    return <ShelvesSkeleton />;
  }

  if (isError || !data) {
    return <CommonError />;
  }

  // Remaining shelves first; sort is stable, so each group keeps its order.
  const shelves = [...data.shelves].sort(
    (left, right) =>
      Number(isShelfCompleted(left)) - Number(isShelfCompleted(right))
  );

  return (
    <PageLayout>
      <div className='page-top-container'>
        <span className='page-top-title'>{t('shelves.title')}</span>

        <NavBreadcrumb crumbs={navigationCrumbs} />
      </div>

      {shelves.length === 0 ? (
        <Centered>
          <p>{t('common.empty')}</p>
        </Centered>
      ) : (
        <div className='grid grid-cols-1 gap-6 px-4 pt-2 pb-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'>
          {shelves.map((shelf) => (
            <ShelveCard
              key={shelf.id}
              shelf={shelf}
              customerId={customerId}
              submissionId={submissionId}
            />
          ))}
        </div>
      )}
    </PageLayout>
  );
};

export default Shelves;
